// mantine
import { Badge, Spoiler, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { DataTableProps } from 'mantine-datatable';
// components
import { Truncate, NumberFormatter } from '@newo/ui-kit';
import { IConversationsUserPersona } from '@newo/api/gateway';
import ChannelByIntegrationIdn from '@/components/ChannelByIntegrationIdn';
import Actions from '@/pages/Contacts/components/Actions';
// utils
import { formatDate } from '@/utils/format.ts';
import { DATE_FORMAT, INTEGRATION_TO_CHANNEL } from '@/constants';

const NAME_COLUMN_WIDTH = 150;
export const useColumns = () => {
    const theme = useMantineTheme();
    const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm})`);

    const columns: DataTableProps<IConversationsUserPersona>['columns'] = [
        {
            accessor: 'name',
            title: 'Contact',
            width: isDesktop ? undefined : NAME_COLUMN_WIDTH,
            cellsClassName: 'truncate',
            render: (contact) => {
                return contact.name ?? '';
            }
        },
        {
            title: 'Last Activity Date',
            accessor: 'last_act_time',
            render: (contact) => {
                return (
                    <span className="whitespace-nowrap">
                        {formatDate(
                            contact.last_act_time,
                            DATE_FORMAT.SHORT_12H
                        )}
                    </span>
                );
            }
        },
        {
            title: 'Channel',
            accessor: 'channel',
            render: (contact) => {
                const integrationIdn = contact.actors[0]?.integration_idn ?? '';
                return (
                    <ChannelByIntegrationIdn
                        channel={INTEGRATION_TO_CHANNEL[integrationIdn]}
                    />
                );
            }
        },
        {
            title: 'Contact Information',
            accessor: 'contact_information',
            render: ({ actors }) => {
                return (
                    <Truncate
                        mode="start"
                        startLength={12}
                        text={actors[0]?.contact_information ?? '-'}
                    />
                );
            }
        },
        {
            title: 'External ID',
            accessor: 'externalId',
            render: ({ actors }) => {
                return (
                    <Truncate
                        mode="start"
                        startLength={12}
                        text={actors[0]?.external_id ?? '-'}
                    />
                );
            }
        },
        {
            title: 'Last Message',
            accessor: 'last_act_text',
            render: (contact) => {
                return (
                    <Spoiler
                        className="text-xs"
                        hideLabel="Hide"
                        maxHeight={100}
                        showLabel="Show"
                    >
                        <div className="text-xs">{contact.last_act_text}</div>
                    </Spoiler>
                );
            }
        },
        {
            title: 'Turns',
            accessor: 'act_count',
            render: (contact) => {
                return (
                    <Badge
                        className="bg-gray-2 font-bold text-gray-8 [&>span]:overflow-visible"
                        size="md"
                    >
                        <NumberFormatter value={contact.act_count} />
                    </Badge>
                );
            }
        },
        {
            title: 'Actions',
            accessor: 'actions',
            render: (contact) => {
                return (
                    <Actions
                        contact={contact}
                        sessionId={contact.last_session_id}
                    />
                );
            }
        }
    ];
    return columns;
};
